import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  TablePagination,
  InputAdornment,
  Divider,
  Tooltip,
  FormHelperText,
  Badge,
  Avatar,
  CardActions,
  Fade,
  Grow,
  Zoom,
  useTheme,
  alpha,
} from '@mui/material';
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  MenuBook as MenuIcon,
  SubdirectoryArrowRight as SubcategoryIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Upload as UploadIcon,
  RestaurantMenu as RestaurantIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Use environment variable for API base URL with fallback
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

console.debug('[AdminDashboard] Environment loaded - API_BASE_URL:', API_BASE_URL);

// Helper function to sanitize input
const sanitizeInput = (input) => {
  if (!input) return '';
  // Only remove < and > characters to prevent XSS
  return input.replace(/[<>]/g, '');
};

// Validation functions
const validateItem = (item) => {
  const errors = {};
  if (!item.name?.trim()) errors.name = 'Item name is required';
  if (!item.category_id) errors.category_id = 'Category is required';
  if (!item.price || parseFloat(item.price) <= 0) errors.price = 'Valid price is required';
  if (item.price && parseFloat(item.price) > 999999999) errors.price = 'Price is too high';
  if (item.description && item.description.length > 500) errors.description = 'Description too long (max 500 characters)';
  return errors;
};

const validateCategory = (category) => {
  const errors = {};
  if (!category.name?.trim()) errors.name = 'Category name is required';
  if (category.name && category.name.length > 100) errors.name = 'Category name too long (max 100 characters)';
  return errors;
};

const validateSubcategory = (subcategory) => {
  const errors = {};
  if (!subcategory.name?.trim()) errors.name = 'Subcategory name is required';
  if (!subcategory.category_id) errors.category_id = 'Category is required';
  if (subcategory.name && subcategory.name.length > 255) errors.name = 'Subcategory name too long (max 255 characters)';
  return errors;
};

const AdminDashboard = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [menuData, setMenuData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [subcategoryDialogOpen, setSubcategoryDialogOpen] = useState(false);
  
  // Editing states
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  
  // Category filter states
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [subcategorySearchTerm, setSubcategorySearchTerm] = useState('');
  
  // Form states
  const [itemFormData, setItemFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    subcategory_id: '',
    image: null,
  });
  
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    icon: '',
    sort_order: '',
  });
  
  const [subcategoryFormData, setSubcategoryFormData] = useState({
    name: '',
    category_id: '',
    image: null,
  });
  
  // Form errors
  const [itemErrors, setItemErrors] = useState({});
  const [categoryErrors, setCategoryErrors] = useState({});
  const [subcategoryErrors, setSubcategoryErrors] = useState({});
  
  // Debug console
  console.debug('[AdminDashboard] Component mounted');
  console.debug('[AdminDashboard] API Base URL:', API_BASE_URL);

  // Show snackbar notification
  const showSnackbar = useCallback((message, severity = 'success') => {
    console.debug(`[AdminDashboard] Snackbar: ${severity} - ${message}`);
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar(prev => ({ ...prev, open: false })), 6000);
  }, []);

  // Fetch all menu data (categories with items)
  const fetchData = useCallback(async () => {
    setLoading(true);
    console.debug('[AdminDashboard] Fetching menu data from:', `${API_BASE_URL}/api_menu_items.php?action=categories`);
    try {
      const response = await axios.get(`${API_BASE_URL}/api_menu_items.php?action=categories`, {
        timeout: 30000,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      console.debug('[AdminDashboard] Menu data received:', response.data);
      
      if (response.data.success) {
        setMenuData(response.data.data);
      } else {
        showSnackbar(response.data.message || 'Failed to fetch menu data', 'error');
      }
    } catch (error) {
      console.error('[AdminDashboard] Error fetching menu data:', error);
      let errorMsg = 'Error fetching menu data: ';
      if (error.code === 'ERR_NETWORK') {
        errorMsg += 'Cannot connect to server. Please check if the API is running.';
      } else if (error.response?.status === 404) {
        errorMsg += 'API endpoint not found. Please check API URL configuration.';
      } else {
        errorMsg += error.response?.data?.message || error.message;
      }
      showSnackbar(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  // Fetch all categories
  const fetchCategories = useCallback(async () => {
    console.debug('[AdminDashboard] Fetching categories from:', `${API_BASE_URL}/api_categories.php?action=all`);
    try {
      const response = await axios.get(`${API_BASE_URL}/api_categories.php?action=all`, {
        timeout: 30000,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      console.debug('[AdminDashboard] Categories received:', response.data);
      
      if (response.data.success) {
        setCategories(response.data.data);
      } else {
        console.warn('[AdminDashboard] Failed to fetch categories:', response.data.message);
      }
    } catch (error) {
      console.error('[AdminDashboard] Error fetching categories:', error);
    }
  }, []);

  // Fetch all subcategories for display
  const fetchAllSubcategories = useCallback(async () => {
    console.debug('[AdminDashboard] Fetching all subcategories');
    try {
      const allSubs = [];
      for (const category of categories) {
        const response = await axios.get(`${API_BASE_URL}/api_subcategories.php?action=by_category&category_id=${category.id}`, {
          timeout: 30000,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        if (response.data.success && response.data.data) {
          const subsWithCategory = response.data.data.map(sub => ({
            ...sub,
            category_name: category.name
          }));
          allSubs.push(...subsWithCategory);
        }
      }
      console.debug('[AdminDashboard] All subcategories fetched:', allSubs.length);
      setAllSubcategories(allSubs);
    } catch (error) {
      console.error('[AdminDashboard] Error fetching all subcategories:', error);
    }
  }, [categories]);

  // Fetch subcategories for a specific category (for forms)
  const fetchSubcategoriesForCategory = useCallback(async (categoryId) => {
    if (!categoryId) {
      return [];
    }
    console.debug(`[AdminDashboard] Fetching subcategories for category ${categoryId}`);
    try {
      const response = await axios.get(`${API_BASE_URL}/api_subcategories.php?action=by_category&category_id=${categoryId}`, {
        timeout: 30000,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      console.debug('[AdminDashboard] Subcategories received:', response.data);
      
      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('[AdminDashboard] Error fetching subcategories:', error);
      return [];
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchData();
    fetchCategories();
  }, [fetchData, fetchCategories]);

  // Fetch all subcategories when categories change
  useEffect(() => {
    if (categories.length > 0) {
      fetchAllSubcategories();
    }
  }, [categories, fetchAllSubcategories]);

  // Handle category change in item form
  const handleCategoryChange = async (e) => {
    const categoryId = e.target.value;
    setItemFormData(prev => ({ ...prev, category_id: categoryId, subcategory_id: '' }));
    const subs = await fetchSubcategoriesForCategory(categoryId);
    // We don't store in state, just use for the select
  };

  // Handle item form input changes
  const handleItemInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image' && files) {
      setItemFormData(prev => ({ ...prev, image: files[0] }));
      console.debug('[AdminDashboard] Image selected:', files[0].name);
    } else {
      const sanitizedValue = name === 'name' || name === 'description' ? sanitizeInput(value) : value;
      setItemFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    }
    // Clear error for this field
    if (itemErrors[name]) {
      setItemErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle category form input changes
  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = name === 'name' ? sanitizeInput(value) : value;
    setCategoryFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    if (categoryErrors[name]) {
      setCategoryErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle subcategory form input changes
  const handleSubcategoryInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image' && files) {
      setSubcategoryFormData(prev => ({ ...prev, image: files[0] }));
      console.debug('[AdminDashboard] Subcategory image selected:', files[0].name);
    } else {
      const sanitizedValue = name === 'name' ? sanitizeInput(value) : value;
      setSubcategoryFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    }
    if (subcategoryErrors[name]) {
      setSubcategoryErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Open dialog for adding/editing item
  const handleOpenItemDialog = async (item = null) => {
    console.debug('[AdminDashboard] Opening item dialog', item ? 'for edit' : 'for create');
    setItemErrors({});
    if (item) {
      setEditingItem(item);
      setItemFormData({
        name: item.name || '',
        description: item.description || '',
        price: item.price || '',
        category_id: item.category_id || '',
        subcategory_id: item.subcategory_id || '',
        image: null,
      });
    } else {
      setEditingItem(null);
      setItemFormData({
        name: '',
        description: '',
        price: '',
        category_id: '',
        subcategory_id: '',
        image: null,
      });
    }
    setItemDialogOpen(true);
  };

  // Open dialog for adding/editing category
  const handleOpenCategoryDialog = (category = null) => {
    console.debug('[AdminDashboard] Opening category dialog', category ? 'for edit' : 'for create');
    setCategoryErrors({});
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({
        name: category.name || '',
        icon: category.icon || '',
        sort_order: category.sort_order || '',
      });
    } else {
      setEditingCategory(null);
      setCategoryFormData({
        name: '',
        icon: '',
        sort_order: '',
      });
    }
    setCategoryDialogOpen(true);
  };

  // Open dialog for adding/editing subcategory
  const handleOpenSubcategoryDialog = (subcategory = null) => {
    console.debug('[AdminDashboard] Opening subcategory dialog', subcategory ? 'for edit' : 'for create');
    setSubcategoryErrors({});
    if (subcategory) {
      setEditingSubcategory(subcategory);
      setSubcategoryFormData({
        name: subcategory.name || '',
        category_id: subcategory.category_id || '',
        image: null,
      });
    } else {
      setEditingSubcategory(null);
      setSubcategoryFormData({
        name: '',
        category_id: '',
        image: null,
      });
    }
    setSubcategoryDialogOpen(true);
  };

  // Handle item form submission (Create/Update)
  const handleItemSubmit = async () => {
    console.debug('[AdminDashboard] Submitting item form...', itemFormData);
    
    const errors = validateItem(itemFormData);
    if (Object.keys(errors).length > 0) {
      setItemErrors(errors);
      showSnackbar(Object.values(errors)[0], 'error');
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', itemFormData.name.trim());
      formDataToSend.append('description', itemFormData.description || '');
      formDataToSend.append('price', parseFloat(itemFormData.price));
      formDataToSend.append('category_id', itemFormData.category_id);
      if (itemFormData.subcategory_id) {
        formDataToSend.append('subcategory_id', itemFormData.subcategory_id);
      }
      if (itemFormData.image) {
        formDataToSend.append('image', itemFormData.image);
      }

      let response;
      if (editingItem) {
        console.debug('[AdminDashboard] Updating item:', editingItem.id);
        response = await axios.post(`${API_BASE_URL}/api_menu_items.php?id=${editingItem.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: { _method: 'PUT' }
        });
      } else {
        console.debug('[AdminDashboard] Creating new item');
        response = await axios.post(`${API_BASE_URL}/api_menu_items.php`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      console.debug('[AdminDashboard] Submit response:', response.data);

      if (response.data.success) {
        showSnackbar(editingItem ? 'Item updated successfully' : 'Item created successfully', 'success');
        setItemDialogOpen(false);
        await fetchData();
        await fetchCategories();
      } else {
        showSnackbar(response.data.message || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('[AdminDashboard] Submit error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error occurred';
      showSnackbar(`Error: ${errorMsg}`, 'error');
    }
  };

  // Handle category form submission
  const handleCategorySubmit = async () => {
    console.debug('[AdminDashboard] Submitting category form...', categoryFormData);
    
    const errors = validateCategory(categoryFormData);
    if (Object.keys(errors).length > 0) {
      setCategoryErrors(errors);
      showSnackbar(Object.values(errors)[0], 'error');
      return;
    }

    try {
      let response;
      const categoryData = {
        name: categoryFormData.name.trim(),
        icon: categoryFormData.icon || null,
        sort_order: categoryFormData.sort_order ? parseInt(categoryFormData.sort_order) : null,
      };

      if (editingCategory) {
        console.debug('[AdminDashboard] Updating category:', editingCategory.id);
        response = await axios.put(`${API_BASE_URL}/api_categories.php`, {
          ...categoryData,
          id: editingCategory.id
        });
      } else {
        console.debug('[AdminDashboard] Creating new category');
        const formData = new FormData();
        formData.append('name', categoryData.name);
        formData.append('icon', categoryData.icon);
        formData.append('sort_order', categoryData.sort_order);
        response = await axios.post(`${API_BASE_URL}/api_categories.php`, formData);
      }

      console.debug('[AdminDashboard] Category submit response:', response.data);

      if (response.data.success) {
        showSnackbar(editingCategory ? 'Category updated successfully' : 'Category created successfully', 'success');
        setCategoryDialogOpen(false);
        await fetchCategories();
        await fetchData();
      } else {
        showSnackbar(response.data.message || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('[AdminDashboard] Category submit error:', error);
      showSnackbar(`Error: ${error.response?.data?.message || error.message}`, 'error');
    }
  };

  // Handle subcategory form submission
  const handleSubcategorySubmit = async () => {
    console.debug('[AdminDashboard] Submitting subcategory form...', subcategoryFormData);
    
    const errors = validateSubcategory(subcategoryFormData);
    if (Object.keys(errors).length > 0) {
      setSubcategoryErrors(errors);
      showSnackbar(Object.values(errors)[0], 'error');
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', subcategoryFormData.name.trim());
      formDataToSend.append('category_id', subcategoryFormData.category_id);
      if (subcategoryFormData.image) {
        formDataToSend.append('image', subcategoryFormData.image);
      }

      let response;
      if (editingSubcategory) {
        console.debug('[AdminDashboard] Updating subcategory:', editingSubcategory.id);
        response = await axios.put(`${API_BASE_URL}/api_subcategories.php`, {
          id: editingSubcategory.id,
          name: subcategoryFormData.name.trim()
        });
      } else {
        console.debug('[AdminDashboard] Creating new subcategory');
        response = await axios.post(`${API_BASE_URL}/api_subcategories.php`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      console.debug('[AdminDashboard] Subcategory submit response:', response.data);

      if (response.data.success) {
        showSnackbar(editingSubcategory ? 'Subcategory updated successfully' : 'Subcategory created successfully', 'success');
        setSubcategoryDialogOpen(false);
        await fetchCategories();
        await fetchData();
        await fetchAllSubcategories();
      } else {
        showSnackbar(response.data.message || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('[AdminDashboard] Subcategory submit error:', error);
      showSnackbar(`Error: ${error.response?.data?.message || error.message}`, 'error');
    }
  };

  // Handle delete item
  const handleDeleteItem = async (id, name) => {
    console.debug(`[AdminDashboard] Deleting item: ${id} - ${name}`);
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/api_menu_items.php?id=${id}`);
        console.debug('[AdminDashboard] Delete response:', response.data);
        
        if (response.data.success) {
          showSnackbar('Item deleted successfully', 'success');
          await fetchData();
        } else {
          showSnackbar(response.data.message || 'Delete failed', 'error');
        }
      } catch (error) {
        console.error('[AdminDashboard] Delete error:', error);
        showSnackbar('Error deleting item: ' + (error.response?.data?.message || error.message), 'error');
      }
    }
  };

  // Handle delete category
  const handleDeleteCategory = async (id, name) => {
    console.debug(`[AdminDashboard] Deleting category: ${id} - ${name}`);
    if (window.confirm(`Are you sure you want to delete category "${name}"? This will also delete all subcategories and items in it.`)) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/api_categories.php?id=${id}`);
        console.debug('[AdminDashboard] Delete category response:', response.data);
        
        if (response.data.success) {
          showSnackbar('Category deleted successfully', 'success');
          await fetchCategories();
          await fetchData();
          await fetchAllSubcategories();
        } else {
          showSnackbar(response.data.message || 'Delete failed', 'error');
        }
      } catch (error) {
        console.error('[AdminDashboard] Delete category error:', error);
        showSnackbar('Error deleting category: ' + (error.response?.data?.message || error.message), 'error');
      }
    }
  };

  // Handle delete subcategory
  const handleDeleteSubcategory = async (id, name) => {
    console.debug(`[AdminDashboard] Deleting subcategory: ${id} - ${name}`);
    if (window.confirm(`Are you sure you want to delete subcategory "${name}"? This will also delete all items in it.`)) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/api_subcategories.php?id=${id}`);
        console.debug('[AdminDashboard] Delete subcategory response:', response.data);
        
        if (response.data.success) {
          showSnackbar('Subcategory deleted successfully', 'success');
          await fetchCategories();
          await fetchData();
          await fetchAllSubcategories();
        } else {
          showSnackbar(response.data.message || 'Delete failed', 'error');
        }
      } catch (error) {
        console.error('[AdminDashboard] Delete subcategory error:', error);
        showSnackbar('Error deleting subcategory: ' + (error.response?.data?.message || error.message), 'error');
      }
    }
  };

  // Get all menu items flattened for table display with filters
  const getAllItems = useCallback(() => {
    const items = [];
    if (!Array.isArray(menuData)) return items;
    
    menuData.forEach(category => {
      // Items directly under category
      if (category.items && Array.isArray(category.items)) {
        category.items.forEach(item => {
          items.push({
            ...item,
            category_name: category.name,
            subcategory_name: null,
            type: 'direct'
          });
        });
      }
      
      // Items under subcategories
      if (category.subcategories && Array.isArray(category.subcategories)) {
        category.subcategories.forEach(subcategory => {
          if (subcategory.items && Array.isArray(subcategory.items)) {
            subcategory.items.forEach(item => {
              items.push({
                ...item,
                category_name: category.name,
                subcategory_name: subcategory.name,
                type: 'subcategory'
              });
            });
          }
        });
      }
    });
    return items;
  }, [menuData]);

  // Get subcategory items for display
  const getSubcategoryItems = useCallback(() => {
    const items = [];
    if (!Array.isArray(menuData)) return items;
    
    menuData.forEach(category => {
      if (category.subcategories && Array.isArray(category.subcategories)) {
        category.subcategories.forEach(subcategory => {
          items.push({
            id: subcategory.id,
            name: subcategory.name,
            category_id: category.id,
            category_name: category.name,
            image_url: subcategory.image_url,
            items_count: subcategory.items ? subcategory.items.length : 0
          });
        });
      }
    });
    return items;
  }, [menuData]);

  // Apply filters to items
  const getFilteredItems = useCallback(() => {
    let items = getAllItems();
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(item => 
        item.name?.toLowerCase().includes(term) ||
        item.category_name?.toLowerCase().includes(term) ||
        (item.subcategory_name && item.subcategory_name.toLowerCase().includes(term)) ||
        item.description?.toLowerCase().includes(term)
      );
    }
    
    // Category filter
    if (categoryFilter) {
      items = items.filter(item => item.category_id === parseInt(categoryFilter));
    }
    
    // Subcategory filter
    if (subcategoryFilter) {
      items = items.filter(item => item.subcategory_id === parseInt(subcategoryFilter));
    }
    
    // Price range filter
    if (priceRange.min) {
      items = items.filter(item => item.price >= parseFloat(priceRange.min));
    }
    if (priceRange.max) {
      items = items.filter(item => item.price <= parseFloat(priceRange.max));
    }
    
    return items;
  }, [getAllItems, searchTerm, categoryFilter, subcategoryFilter, priceRange]);

  // Filter categories
  const getFilteredCategories = useCallback(() => {
    if (!categorySearchTerm) return categories;
    const term = categorySearchTerm.toLowerCase();
    return categories.filter(cat => 
      cat.name?.toLowerCase().includes(term)
    );
  }, [categories, categorySearchTerm]);

  // Filter subcategories from allSubcategories
  const getFilteredSubcategories = useCallback(() => {
    if (!subcategorySearchTerm) return allSubcategories;
    const term = subcategorySearchTerm.toLowerCase();
    return allSubcategories.filter(sub => 
      sub.name?.toLowerCase().includes(term) ||
      sub.category_name?.toLowerCase().includes(term)
    );
  }, [allSubcategories, subcategorySearchTerm]);

  const filteredItems = getFilteredItems();
  const paginatedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const filteredCategories = getFilteredCategories();
  const filteredSubcategoriesList = getFilteredSubcategories();

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setSubcategoryFilter('');
    setPriceRange({ min: '', max: '' });
    setPage(0);
    showSnackbar('Filters cleared', 'info');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Grow in={true} timeout={500}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 4, background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' }}>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h3" component="h1" sx={{ 
                  fontWeight: 'bold', 
                  background: 'linear-gradient(135deg, #2e7d32, #4caf50)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  mb: 1
                }}>
                  Menu Management Dashboard
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  Manage your restaurant's menu items, categories, and subcategories
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    fetchData();
                    fetchCategories();
                    fetchAllSubcategories();
                  }}
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenItemDialog()}
                  sx={{ 
                    background: 'linear-gradient(135deg, #4caf50, #2e7d32)',
                    borderRadius: 2,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #45a049, #1b5e20)',
                    }
                  }}
                >
                  Add New Item
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CategoryIcon />}
                  onClick={() => handleOpenCategoryDialog()}
                  sx={{ 
                    background: 'linear-gradient(135deg, #2196f3, #1976d2)',
                    borderRadius: 2,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1e88e5, #1565c0)',
                    }
                  }}
                >
                  Add Category
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SubcategoryIcon />}
                  onClick={() => handleOpenSubcategoryDialog()}
                  sx={{ 
                    background: 'linear-gradient(135deg, #ff9800, #f57c00)',
                    borderRadius: 2,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #fb8c00, #ef6c00)',
                    }
                  }}
                >
                  Add Subcategory
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Tabs */}
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
            TabIndicatorProps={{ sx: { backgroundColor: '#4caf50', height: 3 } }}
          >
            <Tab 
              icon={<MenuIcon />} 
              iconPosition="start"
              label={`Menu Items (${filteredItems.length})`} 
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '1rem', minWidth: 200 }}
            />
            <Tab 
              icon={<CategoryIcon />} 
              iconPosition="start"
              label={`Categories (${filteredCategories.length})`} 
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '1rem', minWidth: 200 }}
            />
            <Tab 
              icon={<SubcategoryIcon />} 
              iconPosition="start"
              label={`Subcategories (${filteredSubcategoriesList.length})`} 
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '1rem', minWidth: 200 }}
            />
          </Tabs>

          {/* Menu Items Tab */}
          {activeTab === 0 && (
            <Fade in={activeTab === 0} timeout={300}>
              <Box>
                {/* Filters Section - Always Visible */}
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3, backgroundColor: alpha(theme.palette.primary.main, 0.02) }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FilterListIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Filters & Search</Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="medium"
                        placeholder="Search by name, category, subcategory..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setPage(0);
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                          endAdornment: searchTerm && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setSearchTerm('')}>
                                <ClearIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="medium" sx={{ minWidth: 150 }}>
                        <InputLabel>Category Filter</InputLabel>
                        <Select
                          value={categoryFilter}
                          onChange={(e) => {
                            setCategoryFilter(e.target.value);
                            setPage(0);
                          }}
                          label="Category Filter"
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="">All Categories</MenuItem>
                          {categories.map(cat => (
                            <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="medium" sx={{ minWidth: 150 }}>
                        <InputLabel>Subcategory Filter</InputLabel>
                        <Select
                          value={subcategoryFilter}
                          onChange={(e) => {
                            setSubcategoryFilter(e.target.value);
                            setPage(0);
                          }}
                          label="Subcategory Filter"
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="">All Subcategories</MenuItem>
                          {allSubcategories.map(sub => (
                            <MenuItem key={sub.id} value={sub.id}>{sub.name} ({sub.category_name})</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="medium"
                        type="number"
                        label="Min Price (₦)"
                        value={priceRange.min}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                        sx={{ borderRadius: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Button 
                        fullWidth 
                        variant="outlined" 
                        onClick={clearFilters}
                        sx={{ height: '56px', borderRadius: 2, borderColor: '#dc3545', color: '#dc3545' }}
                      >
                        Clear
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Items Table */}
                <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto', boxShadow: 2 }}>
                  <Table sx={{ minWidth: 800 }}>
                    <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Subcategory</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Price (₦)</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Image</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                            <InventoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary">
                              No menu items found
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Try adjusting your filters or add a new item
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedItems.map((item, index) => (
                          <TableRow key={item.id} hover sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.02) } }}>
                            <TableCell>{item.id}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={item.category_name} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                                sx={{ borderRadius: 1.5 }}
                              />
                            </TableCell>
                            <TableCell>
                              {item.subcategory_name ? (
                                <Chip 
                                  label={item.subcategory_name} 
                                  size="small" 
                                  variant="outlined"
                                  color="secondary"
                                  sx={{ borderRadius: 1.5 }}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                                ₦{parseFloat(item.price).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={item.description || ''}>
                                <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {item.description || '-'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              {item.image_url ? (
                                <Avatar 
                                  src={`${API_BASE_URL.replace('/api', '')}/uploads/${item.image_url}`}
                                  variant="rounded"
                                  sx={{ width: 50, height: 50 }}
                                >
                                  <RestaurantIcon />
                                </Avatar>
                              ) : '-'}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Edit">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleOpenItemDialog(item)}
                                  size="small"
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  color="error"
                                  onClick={() => handleDeleteItem(item.id, item.name)}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={filteredItems.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </TableContainer>
              </Box>
            </Fade>
          )}

          {/* Categories Tab */}
          {activeTab === 1 && (
            <Fade in={activeTab === 1} timeout={300}>
              <Box>
                {/* Category Search */}
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                  <TextField
                    fullWidth
                    size="medium"
                    placeholder="Search categories by name..."
                    value={categorySearchTerm}
                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                      endAdornment: categorySearchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setCategorySearchTerm('')}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Paper>
                
                <Grid container spacing={3}>
                  {filteredCategories.length === 0 ? (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
                        <CategoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary">
                          No categories found
                        </Typography>
                      </Paper>
                    </Grid>
                  ) : (
                    filteredCategories.map((category, index) => (
                      <Zoom in={true} key={category.id} style={{ transitionDelay: `${index * 50}ms` }}>
                        <Grid item xs={12} md={6} lg={4}>
                          <Card sx={{ 
                            borderRadius: 3, 
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { 
                              transform: 'translateY(-8px)',
                              boxShadow: 6
                            }
                          }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                                    <CategoryIcon />
                                  </Avatar>
                                  <Typography variant="h6" fontWeight={600}>
                                    {category.name}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => handleOpenCategoryDialog(category)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteCategory(category.id, category.name)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                              <Divider sx={{ my: 1.5 }} />
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <strong>Sort Order:</strong> {category.sort_order || 'Default'}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <strong>Icon:</strong> {category.icon || 'None'}
                                </Typography>
                              </Box>
                              <Chip 
                                label={`ID: ${category.id}`} 
                                size="small" 
                                sx={{ mt: 2, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      </Zoom>
                    ))
                  )}
                </Grid>
              </Box>
            </Fade>
          )}

          {/* Subcategories Tab */}
          {activeTab === 2 && (
            <Fade in={activeTab === 2} timeout={300}>
              <Box>
                {/* Subcategory Search */}
                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                  <TextField
                    fullWidth
                    size="medium"
                    placeholder="Search subcategories by name or category..."
                    value={subcategorySearchTerm}
                    onChange={(e) => setSubcategorySearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                      endAdornment: subcategorySearchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSubcategorySearchTerm('')}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Paper>
                
                <Grid container spacing={3}>
                  {filteredSubcategoriesList.length === 0 ? (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
                        <SubcategoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary">
                          No subcategories found
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenSubcategoryDialog()}
                          sx={{ mt: 2 }}
                        >
                          Add Subcategory
                        </Button>
                      </Paper>
                    </Grid>
                  ) : (
                    filteredSubcategoriesList.map((subcategory, index) => (
                      <Zoom in={true} key={subcategory.id} style={{ transitionDelay: `${index * 50}ms` }}>
                        <Grid item xs={12} md={6} lg={4}>
                          <Card sx={{ 
                            borderRadius: 3, 
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { 
                              transform: 'translateY(-8px)',
                              boxShadow: 6
                            }
                          }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main }}>
                                    <SubcategoryIcon />
                                  </Avatar>
                                  <Typography variant="h6" fontWeight={600}>
                                    {subcategory.name}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => handleOpenSubcategoryDialog(subcategory)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteSubcategory(subcategory.id, subcategory.name)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                              <Divider sx={{ my: 1.5 }} />
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <strong>Category:</strong> 
                                  <Chip 
                                    label={subcategory.category_name} 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <strong>Items:</strong> {subcategory.items_count || 0}
                                </Typography>
                              </Box>
                              {subcategory.image_url && (
                                <Box sx={{ mt: 2 }}>
                                  <Avatar 
                                    src={`${API_BASE_URL.replace('/api', '')}/uploads/${subcategory.image_url}`}
                                    variant="rounded"
                                    sx={{ width: 60, height: 60 }}
                                  />
                                </Box>
                              )}
                              <Chip 
                                label={`ID: ${subcategory.id}`} 
                                size="small" 
                                sx={{ mt: 2, backgroundColor: alpha(theme.palette.secondary.main, 0.1) }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      </Zoom>
                    ))
                  )}
                </Grid>
              </Box>
            </Fade>
          )}
        </Paper>
      </Grow>

      {/* Add/Edit Item Dialog */}
      <Dialog open={itemDialogOpen} onClose={() => setItemDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #4caf50, #2e7d32)', color: 'white' }}>
          {editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Item Name"
            name="name"
            value={itemFormData.name}
            onChange={handleItemInputChange}
            margin="normal"
            required
            autoFocus
            error={!!itemErrors.name}
            helperText={itemErrors.name}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth margin="normal" required error={!!itemErrors.category_id} sx={{ mb: 2, minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              name="category_id"
              value={itemFormData.category_id}
              onChange={handleCategoryChange}
              label="Category"
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
            {itemErrors.category_id && <FormHelperText>{itemErrors.category_id}</FormHelperText>}
          </FormControl>
          
          {itemFormData.category_id && (
            <FormControl fullWidth margin="normal" sx={{ mb: 2, minWidth: 150 }}>
              <InputLabel>Subcategory (Optional)</InputLabel>
              <Select
                name="subcategory_id"
                value={itemFormData.subcategory_id}
                onChange={handleItemInputChange}
                label="Subcategory (Optional)"
              >
                <MenuItem value="">None</MenuItem>
                {allSubcategories.filter(sub => sub.category_id === parseInt(itemFormData.category_id)).map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          <TextField
            fullWidth
            label="Price (₦)"
            name="price"
            type="number"
            value={itemFormData.price}
            onChange={handleItemInputChange}
            margin="normal"
            required
            error={!!itemErrors.price}
            helperText={itemErrors.price}
            InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={itemFormData.description}
            onChange={handleItemInputChange}
            margin="normal"
            multiline
            rows={3}
            error={!!itemErrors.description}
            helperText={itemErrors.description || `${itemFormData.description?.length || 0}/500 characters`}
            sx={{ mb: 2 }}
          />
          
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<UploadIcon />}
            sx={{ mt: 2, mb: 1, borderRadius: 2 }}
          >
            {itemFormData.image ? 'Change Image' : 'Upload Image'}
            <input
              type="file"
              name="image"
              accept="image/*"
              hidden
              onChange={handleItemInputChange}
            />
          </Button>
          {itemFormData.image && (
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'green' }}>
              Selected: {itemFormData.image.name}
            </Typography>
          )}
          {editingItem && editingItem.image_url && !itemFormData.image && (
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'textSecondary' }}>
              Current image: {editingItem.image_url}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setItemDialogOpen(false)} startIcon={<CloseIcon />}>Cancel</Button>
          <Button onClick={handleItemSubmit} variant="contained" color="primary" startIcon={<SaveIcon />}>
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #2196f3, #1976d2)', color: 'white' }}>
          {editingCategory ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Category Name"
            name="name"
            value={categoryFormData.name}
            onChange={handleCategoryInputChange}
            margin="normal"
            required
            autoFocus
            error={!!categoryErrors.name}
            helperText={categoryErrors.name}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Icon (optional)"
            name="icon"
            value={categoryFormData.icon}
            onChange={handleCategoryInputChange}
            margin="normal"
            placeholder="e.g., Pizza, Burger, Drinks"
            helperText="Icon name or emoji for display"
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Sort Order (optional)"
            name="sort_order"
            type="number"
            value={categoryFormData.sort_order}
            onChange={handleCategoryInputChange}
            margin="normal"
            placeholder="Higher number appears first"
            helperText="Leave empty for default ordering"
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCategoryDialogOpen(false)} startIcon={<CloseIcon />}>Cancel</Button>
          <Button onClick={handleCategorySubmit} variant="contained" color="primary" startIcon={<SaveIcon />}>
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Subcategory Dialog */}
      <Dialog open={subcategoryDialogOpen} onClose={() => setSubcategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #ff9800, #f57c00)', color: 'white' }}>
          {editingSubcategory ? 'Edit Subcategory' : 'Add New Subcategory'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <FormControl fullWidth margin="normal" required error={!!subcategoryErrors.category_id} sx={{ mb: 2, minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              name="category_id"
              value={subcategoryFormData.category_id}
              onChange={handleSubcategoryInputChange}
              label="Category"
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
            {subcategoryErrors.category_id && <FormHelperText>{subcategoryErrors.category_id}</FormHelperText>}
          </FormControl>
          
          <TextField
            fullWidth
            label="Subcategory Name"
            name="name"
            value={subcategoryFormData.name}
            onChange={handleSubcategoryInputChange}
            margin="normal"
            required
            error={!!subcategoryErrors.name}
            helperText={subcategoryErrors.name}
            sx={{ mb: 2 }}
          />
          
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<ImageIcon />}
            sx={{ mt: 2, mb: 1, borderRadius: 2 }}
          >
            {subcategoryFormData.image ? 'Change Image' : 'Upload Image (Optional)'}
            <input
              type="file"
              name="image"
              accept="image/*"
              hidden
              onChange={handleSubcategoryInputChange}
            />
          </Button>
          {subcategoryFormData.image && (
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'green' }}>
              Selected: {subcategoryFormData.image.name}
            </Typography>
          )}
          {editingSubcategory && editingSubcategory.image_url && !subcategoryFormData.image && (
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'textSecondary' }}>
              Current image: {editingSubcategory.image_url}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setSubcategoryDialogOpen(false)} startIcon={<CloseIcon />}>Cancel</Button>
          <Button onClick={handleSubcategorySubmit} variant="contained" color="primary" startIcon={<SaveIcon />}>
            {editingSubcategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;